import {
  CheckCircleOutlined,
  CheckOutlined,
  CommentOutlined,
} from '@ant-design/icons';
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  List,
  message,
  Modal,
  Row,
  Select,
  Tabs,
} from 'antd';
import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ClientNavigation } from '../../components/client/client-navigation';
import { CopyableText } from '../../components/shared/CopyableText';

import { Agent, ClientConfig, ClientFeatures, Stage } from '../../types';
import { UserContext } from '../../UserContext';

import { AgentOverrides } from '../../components/client/agent-overrides';
import { AgentInfo } from './AgentInfo';
import { Appointments } from './Appointments';
import CalendarPage from './Calendar';
import Call from './Call';
import { Conversation } from './Conversation';
import { Leads } from './Leads';
import MeetingRecordings from './MeetingRecordings';
import { ProactiveActions } from './ProactiveActions';
import { Reminders } from './Reminders';
import { SupportTickets } from './SupportTickets';
import { Tasks } from './Tasks';
import { formatDateFromUTCtoAgent, getAgentTimezone } from './utils';

const featureLevelOrder = {
  live: 1,
  shadow: 2,
  disabled: 3,
};

type FindByResponse = {
  clientId: string;
  agentId: string;
};

const sortAgents = (agents: Agent[]) => {
  return agents.sort((a, b) => {
    const featureLevelComparison =
      featureLevelOrder[a.feature_level] - featureLevelOrder[b.feature_level];
    if (featureLevelComparison !== 0) {
      return featureLevelComparison;
    }

    const aFirst = a.first_name || '';
    const bFirst = b.first_name || '';
    const firstNameComparison = aFirst.localeCompare(bFirst);
    if (firstNameComparison !== 0) {
      return firstNameComparison;
    }

    const aLast = a.last_name || '';
    const bLast = b.last_name || '';
    return aLast.localeCompare(bLast);
  });
};

const sortTicketsByCreatedAt = (tickets: any[]) => {
  return [...(tickets || [])].sort((a: any, b: any) => {
    const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
    const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
    return dateB - dateA;
  });
};

export const AgentSmsPage = ({ apiUrl }: { apiUrl: string }): JSX.Element => {
  const { clientId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [nameSearch, setNameSearch] = useState<string>('');
  const [filteredAgents, setFilteredAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeAgent, setActiveAgent] = useState<Agent | undefined>(undefined);
  const [friendlyName, setFriendlyName] = useState<string>('');
  const [referralUrl, setReferralUrl] = useState<string>('');
  const [clientConfig, setClientConfig] = useState<ClientConfig | null>(null);
  const [clientFeatures, setClientFeatures] = useState<ClientFeatures | null>(
    null,
  );
  const [stages, setStages] = useState<Stage[]>([]);
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [overridesOpen, setOverridesOpen] = useState<boolean>(false);
  const [supportTickets, setSupportTickets] = useState<any[]>([]);
  const [supportTicketsCount, setSupportTicketsCount] = useState<number>(0);
  const [agentsWithTickets, setAgentsWithTickets] = useState<Set<string>>(
    new Set(),
  );
  const [isSupportTicketsExpanded, setIsSupportTicketsExpanded] =
    useState(false);

  // PagerDuty state
  const [activeIncident, setActiveIncident] = useState<any>(null);
  const [isNoteModalVisible, setIsNoteModalVisible] = useState(false);
  const [isResolveModalVisible, setIsResolveModalVisible] = useState(false);
  const [noteForm] = Form.useForm();
  const [resolveForm] = Form.useForm();
  const [incidentNotes, setIncidentNotes] = useState<any[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);

  const user = useContext(UserContext);
  const navigate = useNavigate();

  const [recentMessages, setRecentMessages] = useState<any[]>([]);

  const fetchRecentMessages = async () => {
    if (!activeAgent?.id || !user?.accessToken) {
      return;
    }
    try {
      const url = `${apiUrl}/api/messaging/agent/${activeAgent.id}?limit=20&offset=0`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${user.accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setRecentMessages(data);
      }
    } catch (e) {
      console.error('Failed to fetch messages', e);
    }
  };

  const agentId = searchParams.get('agentId');
  const tabParam = searchParams.get('tab');

  const tabKeys = {
    SMS: 'sms',
    CALLS: 'calls',
    MEETING_RECORDINGS: 'meeting_recordings',
    SUPPORT_TICKETS: 'support_tickets',
    REMINDERS: 'reminders',
    TASKS: 'tasks',
    APPOINTMENTS: 'appointments',
    AGENT_INFO: 'agent_info',
    PROACTIVE_ACTIONS: 'proactive_actions',
    LEADS: 'leads',
    CALENDAR: 'calendar',
  };

  const [activeKey, setActiveKey] = useState(() => {
    if (tabParam && Object.values(tabKeys).includes(tabParam.toLowerCase())) {
      return tabParam.toLowerCase();
    }
    return tabKeys.SMS;
  });

  // Force re-render when agent changes
  const [agentChangeKey, setAgentChangeKey] = useState(0);

  // PagerDuty API functions
  const fetchIncidentNotes = async (incidentId: string) => {
    if (!user?.accessToken) {
      return;
    }

    try {
      setLoadingNotes(true);
      const response = await fetch(
        `${apiUrl}/api/pagerduty/incidents/${activeIncident.source_id}/notes`,
        {
          headers: {
            Authorization: `Bearer ${user.accessToken}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setIncidentNotes(data.notes || []);
      }
    } catch (error) {
      console.error('Failed to fetch incident notes:', error);
      message.error('Failed to load incident notes');
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleAddNote = async (values: { note: string }) => {
    if (!activeIncident?.id || !user?.accessToken || !user?.name) {
      return;
    }

    try {
      const response = await fetch(
        `${apiUrl}/api/pagerduty/incidents/${activeIncident.source_id}/notes`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.accessToken}`,
          },
          body: JSON.stringify({
            note: values.note,
            email: user.name,
          }),
        },
      );

      if (response.ok) {
        message.success('Note added successfully');
        noteForm.resetFields();
        fetchIncidentNotes(activeIncident.id);
        // Refresh the support tickets list to update conversation thread
        if (agentId) {
          await fetchSupportTickets();
          await fetchSupportTicketsCount();
        }
      } else {
        throw new Error('Failed to add note');
      }
    } catch (error) {
      console.error('Failed to add note:', error);
      message.error('Failed to add note');
    }
  };

  const handleAcknowledgeIncident = async () => {
    if (!activeIncident?.id || !user?.accessToken || !user?.name) {
      message.error('Missing required information to acknowledge incident');
      return;
    }

    if (!activeIncident.source_id) {
      message.error('Ticket source_id is missing. Cannot acknowledge.');
      console.error('Active incident missing source_id:', activeIncident);
      return;
    }

    try {
      const response = await fetch(
        `${apiUrl}/api/pagerduty/incidents/${activeIncident.source_id}/acknowledge`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.accessToken}`,
          },
          body: JSON.stringify({
            email: user.name,
          }),
        },
      );

      if (response.ok) {
        message.success('Incident acknowledged successfully');
        // Refresh the support tickets list
        setSupportTickets([]);
        if (agentId) {
          await fetchSupportTickets();
          await fetchSupportTicketsCount();
        }
      } else {
        const errorText = await response.text();
        console.error(
          'Failed to acknowledge incident:',
          response.status,
          errorText,
        );
        throw new Error('Failed to acknowledge incident');
      }
    } catch (error) {
      console.error('Failed to acknowledge incident:', error);
      message.error('Failed to acknowledge incident');
    }
  };

  const handleResolveIncident = async (values: {
    resolution_note?: string;
    linked_message_id?: string;
  }) => {
    if (!activeIncident?.id || !user?.accessToken || !user?.name) {
      return;
    }

    try {
      const response = await fetch(
        `${apiUrl}/api/pagerduty/incidents/${activeIncident.source_id}/resolve`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.accessToken}`,
          },
          body: JSON.stringify({
            resolution_note: values.resolution_note,
            linked_message_id: values.linked_message_id,
            email: user.name,
          }),
        },
      );

      if (response.ok) {
        message.success('Incident resolved successfully');
        setIsResolveModalVisible(false);
        resolveForm.resetFields();
        // Refresh the support tickets list
        setSupportTickets([]);
        if (agentId) {
          await fetchSupportTickets();
          await fetchSupportTicketsCount();
        }
      } else {
        throw new Error('Failed to resolve incident');
      }
    } catch (error) {
      console.error('Failed to resolve incident:', error);
      message.error('Failed to resolve incident');
    }
  };

  const handleUnifiedSearch = async (value: string) => {
    const trimmed = value.trim();
    if (trimmed === '') {
      setNameSearch('');
      return;
    }
    try {
      const url = `${apiUrl}/api/findbyagent/${encodeURIComponent(trimmed)}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user?.accessToken}`,
        },
      });
      if (response.ok) {
        const findByResponse = (await response.json()) as FindByResponse;
        setNameSearch('');
        navigate(
          `/agentSms/${findByResponse.clientId}?agentId=${findByResponse.agentId}&tab=${activeKey}`,
        );
        return;
      }
      // If not ok, show error message
      message.error(
        'No agent found with that name, ID, email, or phone number',
      );
    } catch (error) {
      console.error('Find by Agent ID/Phone failed:', error);
      message.error('Search failed. Please try again.');
    }
  };

  const handleTabChange = (key: string) => {
    setActiveKey(key);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', key);
    setSearchParams(newParams);
  };

  const fetchAgents = async () => {
    if (!user?.accessToken) {
      return;
    }

    try {
      setLoading(true);
      const url = `${apiUrl}/api/settings/${clientId}`;
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch client settings: ${response.status}`);
      }

      const jsonData = await response.json();
      setClientConfig(jsonData.config);
      setClientFeatures(jsonData.features);
      setStages(jsonData.stages || []);
      setFriendlyName(jsonData.config.friendly_name);
      setReferralUrl(jsonData.config.referral_url);

      const validAgents = ((jsonData.agents as Agent[]) || []).filter(
        (agent: Agent) => agent.first_name != null,
      );
      setAgents(
        validAgents.sort((a, b) => a.first_name.localeCompare(b.first_name)),
      );
    } catch (err) {
      console.error('Error fetching agents:', err);
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, [clientId]);

  useEffect(() => {
    if (agentId && clientConfig?.id) {
      const fetchAgentDetails = async () => {
        try {
          const response = await fetch(`${apiUrl}/api/configs/${clientId}`, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${user?.accessToken}`,
            },
          });
          if (!response.ok) {
            throw new Error(
              `Failed to fetch agent details: ${response.status}`,
            );
          }

          const jsonData = await response.json();
          const updatedAgent = (jsonData.agents || []).find(
            (agent: Agent) => agent.id === agentId,
          );
          if (updatedAgent) {
            setActiveAgent(updatedAgent);

            setTimeout(() => {
              document
                .querySelector(`[data-agent-id="${updatedAgent.id}"]`)
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 0);
          }
        } catch (error) {
          console.error('Error fetching agent details:', error);
        }
      };

      fetchAgentDetails();
    }
  }, [agentId, clientConfig?.id, apiUrl, user?.accessToken]);

  useEffect(() => {
    const searchLower = nameSearch.toLowerCase();
    setFilteredAgents(
      nameSearch === ''
        ? agents
        : agents.filter(agent => {
            const fullName =
              `${agent.first_name} ${agent.last_name}`.toLowerCase();
            const reversedFullName =
              `${agent.last_name} ${agent.first_name}`.toLowerCase();
            return (
              fullName.includes(searchLower) ||
              reversedFullName.includes(searchLower) ||
              searchLower
                .split(' ')
                .every(
                  part =>
                    fullName.includes(part) || reversedFullName.includes(part),
                )
            );
          }),
    );
  }, [nameSearch, agents]);

  useEffect(() => {
    const intervalId = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!agentId && agents.length > 0) {
      const firstAgent = sortAgents(agents)[0];
      setActiveAgent(firstAgent);
      setAgentChangeKey(prev => prev + 1);
      setSearchParams(prev => {
        const params = new URLSearchParams(prev);
        params.set('agentId', firstAgent.id);
        params.set('tab', activeKey);
        return params;
      });
    }
  }, [agents, agentId, activeKey, setSearchParams]);

  const fetchSupportTickets = async () => {
    if (!agentId) {
      return;
    }
    try {
      const response = await fetch(
        `${apiUrl}/api/pagerduty/incidents/${agentId}`,
        {
          headers: {
            Authorization: `Bearer ${user?.accessToken}`,
          },
        },
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch support tickets: ${response.status}`);
      }
      const jsonData = await response.json();
      const sortedTickets = sortTicketsByCreatedAt(jsonData);
      setSupportTickets(sortedTickets);

      // If there are tickets, add the agent to the set of agents with tickets
      if (sortedTickets.length > 0 && agentId) {
        setAgentsWithTickets(prev => new Set(prev).add(agentId));
      }
    } catch (error) {
      console.error('Error fetching support tickets:', error);
    }
  };

  const [allSupportTickets, setAllSupportTickets] = useState<any[]>([]);

  const fetchSupportTicketsCount = async () => {
    if (!agentId) {
      return;
    }
    try {
      const response = await fetch(
        `${apiUrl}/api/pagerduty/incidents/${agentId}/all`,
        {
          headers: {
            Authorization: `Bearer ${user?.accessToken}`,
          },
        },
      );
      if (response.ok) {
        const jsonData = await response.json();
        const sortedTickets = sortTicketsByCreatedAt(jsonData);
        setSupportTicketsCount(sortedTickets.length || 0);
        setAllSupportTickets(sortedTickets);
      }
    } catch (error) {
      console.error('Error fetching support tickets count:', error);
    }
  };

  useEffect(() => {
    if (agentId) {
      void fetchSupportTickets();
      void fetchSupportTicketsCount();
    }
    // Clear tickets when no agent is selected
    setAllSupportTickets([]);
  }, [agentId, apiUrl, user?.accessToken]);

  const sortedAgents = sortAgents(filteredAgents);

  const getTabItems = () => {
    if (!activeAgent) {
      return [];
    }
    return [
      {
        key: tabKeys.SMS,
        label: <span style={{ fontSize: '18px' }}>SMS</span>,
        children: (
          <Conversation
            agent={activeAgent}
            apiUrl={apiUrl}
            clientConfig={clientConfig}
          />
        ),
      },
      {
        key: tabKeys.CALLS,
        label: <span style={{ fontSize: '18px' }}>Calls</span>,
        children: (
          <Call
            agent={activeAgent}
            apiUrl={apiUrl}
            clientConfig={clientConfig}
          />
        ),
      },
      {
        key: tabKeys.MEETING_RECORDINGS,
        label: <span style={{ fontSize: '18px' }}>Meeting Recordings</span>,
        children: (
          <MeetingRecordings
            agent={activeAgent}
            apiUrl={apiUrl}
            clientConfig={clientConfig}
          />
        ),
      },
      {
        key: tabKeys.SUPPORT_TICKETS,
        label: (
          <span style={{ fontSize: '18px' }}>
            Support Tickets{' '}
            {supportTicketsCount > 0 && `(${supportTicketsCount})`}
          </span>
        ),
        children: (
          <SupportTickets
            agent={activeAgent}
            apiUrl={apiUrl}
            clientConfig={clientConfig}
            onReopen={() => {
              void fetchSupportTickets();
              void fetchSupportTicketsCount();
            }}
            tickets={allSupportTickets}
          />
        ),
      },
      {
        key: tabKeys.REMINDERS,
        label: <span style={{ fontSize: '18px' }}>Reminders</span>,
        children: (
          <Reminders
            agent={activeAgent}
            apiUrl={apiUrl}
            clientConfig={clientConfig}
          />
        ),
      },
      {
        key: tabKeys.TASKS,
        label: <span style={{ fontSize: '18px' }}>Tasks</span>,
        children: (
          <Tasks
            agent={activeAgent}
            apiUrl={apiUrl}
            clientConfig={clientConfig}
          />
        ),
      },
      {
        key: tabKeys.APPOINTMENTS,
        label: <span style={{ fontSize: '18px' }}>Appointments</span>,
        children: (
          <Appointments
            agent={activeAgent}
            apiUrl={apiUrl}
            clientConfig={clientConfig}
          />
        ),
      },
      {
        key: tabKeys.PROACTIVE_ACTIONS,
        label: <span style={{ fontSize: '18px' }}>Proactive Actions</span>,
        children: (
          <ProactiveActions
            agent={activeAgent}
            apiUrl={apiUrl}
            clientConfig={clientConfig}
            onViewLead={leadId => {
              setActiveKey(tabKeys.LEADS);
              const newParams = new URLSearchParams(searchParams);
              newParams.set('tab', tabKeys.LEADS);
              newParams.set('lead', leadId);
              setSearchParams(newParams);
            }}
          />
        ),
      },
      {
        key: tabKeys.AGENT_INFO,
        label: <span style={{ fontSize: '18px' }}>Agent Info</span>,
        children: (
          <AgentInfo
            agent={activeAgent}
            apiUrl={apiUrl}
            clientConfig={clientConfig}
          />
        ),
      },
      {
        key: tabKeys.LEADS,
        label: <span style={{ fontSize: '18px' }}>Leads</span>,
        children: (
          <Leads
            accessToken={user?.accessToken || ''}
            agentId={activeAgent?.id || ''}
            apiUrl={apiUrl}
            clientConfig={clientConfig}
            setError={error => console.error(error)}
            stages={stages}
          />
        ),
      },
      {
        key: tabKeys.CALENDAR,
        label: <span style={{ fontSize: '18px' }}>Calendar</span>,
        children: (
          <CalendarPage
            accessToken={user?.accessToken || ''}
            agentId={activeAgent.id}
            apiUrl={apiUrl}
          />
        ),
      },
    ];
  };

  return (
    <div style={{ width: '100%' }}>
      {clientConfig && (
        <ClientNavigation
          activeTab="sms"
          clientId={clientConfig.id}
          clientName={friendlyName || clientConfig.friendly_name}
          crmLink={referralUrl}
          crmType={clientConfig.crm_type}
          isSingleAgent={clientConfig.is_single_agent}
          numericClientId={parseInt(clientId || '0')}
        />
      )}

      <Row gutter={16} style={{ width: '100%' }}>
        <Col sm={4} xs={24}>
          <Input.Search
            allowClear
            enterButton="Search"
            onChange={e => setNameSearch(e.target.value)}
            onSearch={handleUnifiedSearch}
            placeholder="Search by agent name, HW ID, email, or phone number"
            value={nameSearch}
          />
          <List
            dataSource={sortedAgents}
            loading={loading}
            renderItem={item => (
              <List.Item
                data-agent-id={item.id}
                onClick={() => {
                  setActiveAgent(item);
                  setAgentChangeKey(prev => prev + 1);
                  setSearchParams({ agentId: item.id, tab: activeKey });
                }}
                style={{
                  cursor: 'pointer',
                  textAlign: 'left',
                  backgroundColor:
                    item.id === activeAgent?.id
                      ? 'rgba(128, 128, 128, 0.2)'
                      : undefined,
                }}
              >
                <span
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  <span>
                    {item.first_name} {item.last_name}
                  </span>
                  {agentsWithTickets.has(item.id) && (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        marginLeft: '4px',
                      }}
                      title={`${supportTickets.filter(t => t.agent_id === item.id).length} support tickets`}
                    >
                      <span
                        style={{
                          display: 'inline-block',
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: '#ff4d4f',
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ fontSize: '0.85em', color: '#ff4d4f' }}>
                        (
                        {
                          supportTickets.filter(t => t.agent_id === item.id)
                            .length
                        }
                        )
                      </span>
                    </span>
                  )}
                </span>
                {item.is_isa && <span>(ISA)</span>}
                {item.feature_level === 'shadow' && (
                  <span style={{ fontWeight: 'lighter' }}>
                    (shadow mode, do not send sms)
                  </span>
                )}
                {item.feature_level === 'disabled' && (
                  <span style={{ fontWeight: 'lighter' }}>
                    (disabled, do not send sms)
                  </span>
                )}
              </List.Item>
            )}
            style={{ overflow: 'auto', height: '80vh' }}
          />
        </Col>

        <Col sm={1} xs={0}>
          <Divider style={{ height: '95%' }} type="vertical" />
        </Col>

        <Col sm={19} xs={24}>
          <h5 style={{ textAlign: 'right' }}>
            <CopyableText
              text={`${activeAgent?.first_name || ''} ${activeAgent?.last_name || ''}`.trim()}
            >
              {activeAgent?.first_name} {activeAgent?.last_name}
            </CopyableText>{' '}
            {activeAgent?.feature_level === 'shadow' && (
              <span style={{ fontWeight: 'lighter' }}>
                (shadow mode, do not send sms)
              </span>
            )}
          </h5>
          <div style={{ textAlign: 'right', marginBottom: 8 }}>
            <Button
              disabled={!activeAgent}
              onClick={() => setOverridesOpen(true)}
              size="small"
            >
              Feature Overrides
            </Button>
          </div>
          <div style={{ fontSize: '13px', textAlign: 'right' }}>
            {activeAgent?.email && (
              <span>
                Email:{' '}
                <CopyableText text={activeAgent.email}>
                  {activeAgent.email}
                </CopyableText>
              </span>
            )}
            {activeAgent?.email && activeAgent?.phone && ' | '}
            {activeAgent?.phone && (
              <span>
                Phone:{' '}
                <CopyableText text={activeAgent.phone}>
                  {activeAgent.phone}
                </CopyableText>
              </span>
            )}
          </div>
          <div style={{ fontSize: '13px', textAlign: 'right' }}>
            {activeAgent?.source_id && (
              <span>
                Agent CRM ID:{' '}
                <CopyableText text={activeAgent.source_id}>
                  {activeAgent.source_id}
                </CopyableText>
                {' | '}
              </span>
            )}
            {activeAgent?.id && (
              <span>
                Agent HW ID:{' '}
                <CopyableText text={activeAgent.id}>
                  {activeAgent.id}
                </CopyableText>
              </span>
            )}
          </div>
          {activeAgent?.timezone && (
            <div style={{ fontSize: '13px', textAlign: 'right' }}>
              <strong>All times displayed are local to agent</strong> | Agent's
              current time:{' '}
              {currentTime.toLocaleTimeString('en-US', {
                timeZone: activeAgent.timezone,
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              })}{' '}
              {activeAgent.timezone}
            </div>
          )}
          {supportTickets.length > 0 && (
            <div
              style={{
                background: 'linear-gradient(90deg, #f5222d 0%, #ff7875 100%)',
                color: 'white',
                borderRadius: 8,
                padding: '12px 18px',
                boxShadow: '0 4px 24px rgba(255, 77, 79, 0.12)',
                margin: '20px 0',
                cursor: 'pointer',
                marginLeft: 'auto',
                marginRight: 0,
                fontSize: 15,
              }}
            >
              <div
                onClick={e => {
                  e.stopPropagation();
                  setIsSupportTicketsExpanded(!isSupportTicketsExpanded);
                }}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span
                    style={{
                      fontSize: 18,
                      fontWeight: 'bold',
                      marginRight: 10,
                    }}
                  >
                    <svg
                      fill="white"
                      height="1.4em"
                      style={{ verticalAlign: 'middle' }}
                      viewBox="0 0 512 512"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M256 48C141.1 48 48 141.1 48 256s93.1 208 208 208 208-93.1 208-208S370.9 48 256 48zm0 384c-97.1 0-176-78.9-176-176S158.9 80 256 80s176 78.9 176 176-78.9 176-176 176zm-16-272c0-8.8 7.2-16 16-16s16 7.2 16 16v88c0 8.8-7.2 16-16 16s-16-7.2-16-16zm0 176c0-8.8 7.2-16 16-16s16 7.2 16 16-7.2 16-16 16-16-7.2-16-16z" />
                    </svg>{' '}
                    Support Tickets
                  </span>
                  <span style={{ marginLeft: 10, fontSize: 14, opacity: 0.9 }}>
                    {supportTickets.length} total •{' '}
                    {
                      supportTickets.filter(t => t.status === 'Acknowledged')
                        .length
                    }{' '}
                    acknowledged •{' '}
                    {
                      supportTickets.filter(t => t.status === 'Triggered')
                        .length
                    }{' '}
                    triggered
                  </span>
                </div>
                <div style={{ fontSize: 16, fontWeight: 'bold' }}>
                  {isSupportTicketsExpanded ? '−' : '+'}
                </div>
              </div>
              {isSupportTicketsExpanded && (
                <div style={{ marginTop: 12 }}>
                  <Row gutter={[24, 16]}>
                    {supportTickets.map(record => (
                      <Col
                        key={record.id}
                        lg={12}
                        md={12}
                        sm={12}
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          marginBottom: 16,
                        }}
                        xl={12}
                        xs={24}
                      >
                        <div
                          style={{
                            width: '100%',
                            maxWidth: 360,
                            background: 'rgba(255,255,255,0.11)',
                            borderRadius: 6,
                            padding: '14px 18px',
                            boxShadow: '0 1px 6px rgba(200,0,0,0.04)',
                            minHeight: 80,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                          }}
                        >
                          <div
                            style={{
                              fontWeight: 500,
                              fontSize: 16,
                              marginBottom: 2,
                            }}
                          >
                            <Button
                              onClick={() =>
                                window.open(
                                  `https://housewhisper.pagerduty.com/incidents/${record.source_id}`,
                                  '_blank',
                                )
                              }
                              style={{
                                padding: 0,
                                fontSize: 14,
                                color: '#fff',
                                opacity: 0.85,
                                height: 'auto',
                                textDecoration: 'underline',
                              }}
                              type="link"
                            >
                              #{record.incident_number}
                            </Button>
                            <span
                              style={{
                                fontSize: 14,
                                color: '#fff',
                                opacity: 0.85,
                                marginTop: 4,
                                wordBreak: 'break-word',
                                marginLeft: 8,
                              }}
                            >
                              {`created at ${formatDateFromUTCtoAgent(
                                record.created_at,
                                getAgentTimezone(activeAgent, clientConfig),
                              )}`}
                            </span>
                          </div>
                          <div>
                            <span>Status:</span>{' '}
                            <span
                              style={{
                                backgroundColor:
                                  record.status === 'Triggered'
                                    ? '#faad14'
                                    : record.status === 'Acknowledged'
                                      ? '#1890ff'
                                      : record.status === 'Resolved'
                                        ? '#52c41a'
                                        : '#d9d9d9',
                                color: 'white',
                                padding: '2px 10px',
                                borderRadius: 12,
                                fontWeight: 600,
                                fontSize: 13,
                                textTransform: 'capitalize',
                                marginLeft: 5,
                                marginRight: 0,
                              }}
                            >
                              {record.status}
                            </span>
                          </div>
                          {record.description && (
                            <div
                              style={{
                                fontSize: 14,
                                color: '#fff',
                                opacity: 0.85,
                                marginTop: 4,
                                wordBreak: 'break-word',
                              }}
                            >
                              <em>{record.description}</em>
                            </div>
                          )}
                          <div
                            style={{ marginTop: 8, display: 'flex', gap: 8 }}
                          >
                            <Button
                              icon={<CommentOutlined />}
                              onClick={e => {
                                e.stopPropagation();
                                setActiveIncident(record);
                                fetchIncidentNotes(record.id);
                                setIsNoteModalVisible(true);
                              }}
                              size="small"
                            >
                              Add Note
                            </Button>
                            {record.status?.toLowerCase() === 'triggered' && (
                              <Button
                                icon={<CheckCircleOutlined />}
                                onClick={e => {
                                  e.stopPropagation();
                                  setActiveIncident(record);
                                  void handleAcknowledgeIncident();
                                }}
                                size="small"
                                type="default"
                              >
                                Acknowledge
                              </Button>
                            )}
                            {record.status?.toLowerCase() !== 'resolved' && (
                              <Button
                                icon={<CheckOutlined />}
                                onClick={e => {
                                  e.stopPropagation();
                                  setActiveIncident(record);
                                  setIsResolveModalVisible(true);
                                  void fetchRecentMessages();
                                }}
                                size="small"
                                type="primary"
                              >
                                Resolve
                              </Button>
                            )}
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}
            </div>
          )}

          {/* Note Modal */}
          <Modal
            bodyStyle={{ padding: '16px' }}
            footer={null}
            onCancel={() => {
              setIsNoteModalVisible(false);
              noteForm.resetFields();
            }}
            open={isNoteModalVisible}
            title={`Add Note - #${activeIncident?.incident_number || ''}`}
            width={500}
          >
            <Form form={noteForm} layout="vertical" onFinish={handleAddNote}>
              <Form.Item
                name="note"
                rules={[{ required: true, message: 'Please enter a note' }]}
                style={{ marginBottom: '12px' }}
              >
                <Input.TextArea
                  placeholder="Enter your note here..."
                  rows={3}
                  style={{ fontSize: '13px' }}
                />
              </Form.Item>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '8px',
                }}
              >
                <Button
                  onClick={() => setIsNoteModalVisible(false)}
                  size="small"
                >
                  Cancel
                </Button>
                <Button htmlType="submit" size="small" type="primary">
                  Add Note
                </Button>
              </div>
            </Form>

            {loadingNotes ? (
              <div
                style={{
                  marginTop: '16px',
                  color: '#888',
                  fontStyle: 'italic',
                }}
              >
                Loading notes...
              </div>
            ) : (
              incidentNotes.length > 0 && (
                <div
                  style={{
                    marginTop: '16px',
                    borderTop: '1px solid #eee',
                    paddingTop: '16px',
                  }}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      marginBottom: '8px',
                      fontSize: '13px',
                    }}
                  >
                    Previous Notes ({incidentNotes.length})
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                    }}
                  >
                    {incidentNotes.map(note => (
                      <div
                        key={note.id}
                        style={{
                          background: '#f9f9f9',
                          padding: '8px',
                          borderRadius: '4px',
                          border: '1px solid #eee',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: '4px',
                            fontSize: '12px',
                          }}
                        >
                          <strong style={{ color: '#1890ff' }}>
                            👤 {note.user?.summary || 'System'}
                          </strong>
                          <span style={{ color: '#999' }}>
                            {new Date(note.created_at).toLocaleString([], {
                              dateStyle: 'short',
                              timeStyle: 'short',
                            })}
                          </span>
                        </div>
                        <div
                          style={{
                            fontSize: '13px',
                            whiteSpace: 'pre-wrap',
                            color: '#333',
                          }}
                        >
                          {note.content}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            )}
          </Modal>

          {/* Resolve Modal */}
          <Modal
            bodyStyle={{ padding: '16px' }}
            footer={null}
            onCancel={() => {
              setIsResolveModalVisible(false);
              resolveForm.resetFields();
            }}
            open={isResolveModalVisible}
            title={`Resolve Incident #${activeIncident?.incident_number || ''}`}
            width={500}
          >
            <Form
              form={resolveForm}
              layout="vertical"
              onFinish={handleResolveIncident}
            >
              <Form.Item
                label={
                  <span style={{ fontSize: '13px', fontWeight: 600 }}>
                    Link Message (Optional)
                  </span>
                }
                style={{ marginBottom: 12 }}
              >
                <Form.Item name="linked_message_id" noStyle>
                  <Select
                    allowClear
                    placeholder="Select message..."
                    size="middle"
                    style={{ width: '100%', fontSize: '13px' }}
                  >
                    <Select.Option value="">None</Select.Option>
                    {recentMessages
                      .filter(
                        msg =>
                          !msg.incoming &&
                          msg.meta?.message_source === 'sms' &&
                          !msg.meta?.tool_results &&
                          !msg.meta?.type,
                      )
                      .map(msg => (
                        <Select.Option key={msg.id} value={msg.id}>
                          {msg.message.substring(0, 60)}...
                        </Select.Option>
                      ))}
                  </Select>
                </Form.Item>
              </Form.Item>

              <Form.Item
                label={
                  <span style={{ fontSize: '13px', fontWeight: 600 }}>
                    Resolution Notes (Optional)
                  </span>
                }
                name="resolution_note"
                style={{ marginBottom: '16px' }}
              >
                <Input.TextArea
                  placeholder="Describe resolution..."
                  rows={3}
                  style={{ fontSize: '13px' }}
                />
              </Form.Item>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '8px',
                }}
              >
                <Button
                  onClick={() => setIsResolveModalVisible(false)}
                  size="small"
                >
                  Cancel
                </Button>
                <Button
                  htmlType="submit"
                  icon={<CheckOutlined />}
                  size="small"
                  type="primary"
                >
                  Resolve
                </Button>
              </div>
            </Form>
          </Modal>
          <Tabs
            activeKey={activeKey}
            items={getTabItems()}
            key={`${activeAgent?.id}-${agentChangeKey}`} // Force re-render when agent changes
            onChange={handleTabChange}
          />
        </Col>
      </Row>

      {activeAgent && (
        <AgentOverrides
          accessToken={user?.accessToken || ''}
          agent={activeAgent}
          apiUrl={apiUrl}
          clientFeatures={clientFeatures || undefined}
          clientId={clientConfig?.id || ''}
          onAgentUpdated={fetchAgents}
          onClose={() => setOverridesOpen(false)}
          visible={overridesOpen}
        />
      )}
    </div>
  );
};
